import {
    Component,
    Input,
    Output,
    EventEmitter,
    forwardRef,
    Directive,
    TemplateRef,
    ContentChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { stringSearch } from '../../utils/string.utils';

export interface CheckboxSelectOption {
    id: string;
    label: string;
}

/** @docs-private */
@Directive({
    selector: '[cdpCheckboxMultiselectSelectOption]',
    standalone: false
})
export class CheckboxMultiselectOptionTemplateDirective {
    constructor(public template: TemplateRef<unknown>) {}
}

@Component({
    selector: 'cdp-checkbox-multiselect',
    templateUrl: 'checkbox-multiselect.component.html',
    styleUrls: ['checkbox-multiselect.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CheckboxMultiselectComponent),
            multi: true
        }
    ],
    standalone: false
})
export class CheckboxMultiselectComponent implements ControlValueAccessor {
    @Input() public set options(opts: CheckboxSelectOption[]) {
        this._options = opts;
        this.filteredOptions = opts;
    }
    public get options(): CheckboxSelectOption[] {
        return this._options;
    }
    public filteredOptions: CheckboxSelectOption[];
    public searchTerm = '';
    private _options: CheckboxSelectOption[];
    @Input() public hasHelperBtns = true;
    @Input() public hasCancelBtn = true;
    @Input() public hasConfirmBtn = true;
    @Input() public hasSearch = true;
    @Input() public useZebraStripes = true;
    @Output() public valueChange = new EventEmitter();
    @Output() public cancelEvent = new EventEmitter();
    @Output() public confirm = new EventEmitter<CheckboxSelectOption[]>();
    @ContentChild(CheckboxMultiselectOptionTemplateDirective, { read: TemplateRef })
    _optionTemplate: TemplateRef<unknown>;
    public model: CheckboxSelectOption[] = [];
    public onChange: (value: any) => void = () => undefined;
    public onTouch: () => any = () => undefined;

    public writeValue(model: CheckboxSelectOption[]): void {
        this.model = model || [];
    }

    public registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => any): void {
        this.onTouch = fn;
    }

    search(searchTerm: string): void {
        this.searchTerm = searchTerm;
        this.filteredOptions = this.options.filter(option => stringSearch(option.label, searchTerm));
    }

    public optionClicked(option: CheckboxSelectOption): void {
        const idx = this.model.findIndex(e => e.id === option.id);
        if (idx > -1) {
            this.model.splice(idx, 1);
        } else {
            this.model.push(option);
        }
        this.modelValueChanged();
    }

    public selectAll(): void {
        this.model.length = 0;
        this.model.push(...this.options);
        this.modelValueChanged();
    }

    public clearAll(): void {
        this.model.length = 0;
        this.modelValueChanged();
    }

    public optionIsSelected(option: CheckboxSelectOption): boolean {
        return this.model.some(e => e.id === option.id);
    }

    public cancelClicked(): void {
        this.cancelEvent.emit();
    }

    public confirmClicked(): void {
        this.confirm.emit(this.model);
    }

    private modelValueChanged(): void {
        this.onChange(this.model);
        this.valueChange.emit(this.model);
    }
}
